import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { fileIcon, fmtSize } from "./ChallengeSubmitDialog";

const fetchBlobUrl = async (subId, att) => {
  const { data } = await axios.get(`${API}/submissions/${subId}/attachments/${att.id}`, { responseType: "blob" });
  return URL.createObjectURL(new Blob([data], { type: att.content_type }));
};

const isImage = (a) => a.content_type.startsWith("image/") && !/heic|heif/.test(a.content_type);
const isVideo = (a) => a.content_type.startsWith("video/");

const MediaThumb = ({ subId, att, onOpen }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u; let alive = true;
    fetchBlobUrl(subId, att).then((x) => { u = x; if (alive) setUrl(x); }).catch(() => {});
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [subId, att]);
  return (
    <button type="button" onClick={() => url && onOpen(att, url)} data-testid={`attachment-thumb-${att.id}`}
      className="w-24 h-24 rounded-2xl bg-arc overflow-hidden flex items-center justify-center relative hover:-translate-y-0.5 transition-transform">
      {!url && <span className="text-xs text-arc-muted">carregando…</span>}
      {url && isImage(att) && <img src={url} alt={att.filename} className="w-full h-full object-cover" />}
      {url && isVideo(att) && <><video src={url} className="w-full h-full object-cover" muted /><span className="absolute text-2xl">▶️</span></>}
    </button>
  );
};

const FileRow = ({ subId, att }) => {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const url = await fetchBlobUrl(subId, att);
      const a = document.createElement("a");
      a.href = url; a.target = "_blank"; a.rel = "noopener";
      if (!/pdf|text\/plain/.test(att.content_type)) a.download = att.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { alert("Não foi possível abrir o arquivo."); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={open} disabled={busy} data-testid={`attachment-open-${att.id}`}
      className="flex items-center gap-3 p-2 pr-4 rounded-2xl bg-arc hover:bg-white border-2 border-transparent hover:border-[var(--arc-primary)] transition-colors text-left">
      <span className="text-2xl">{fileIcon(att.filename, att.content_type)}</span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-arc-text truncate max-w-[220px]">{att.filename}</span>
        <span className="block text-xs text-arc-muted">{busy ? "abrindo…" : `${fmtSize(att.size)} · abrir`}</span>
      </span>
    </button>
  );
};

export const SubmissionAttachments = ({ sub }) => {
  const [viewer, setViewer] = useState(null);
  const media = (sub.attachments || []).filter((a) => isImage(a) || isVideo(a));
  const files = (sub.attachments || []).filter((a) => !isImage(a) && !isVideo(a));
  const comments = sub.comments || [];
  if (!media.length && !files.length && !comments.length) return <div className="text-xs text-arc-muted mt-2">Sem anexos ou comentários.</div>;
  return (
    <div className="mt-3 space-y-3" data-testid={`submission-attachments-${sub.id}`}>
      {comments.map((c, i) => <div key={i} className="p-3 rounded-2xl bg-arc text-sm text-arc-text" data-testid={`submission-comment-${sub.id}-${i}`}>💬 {c.text}</div>)}
      {media.length > 0 && <div className="flex flex-wrap gap-2">{media.map((a) => <MediaThumb key={a.id} subId={sub.id} att={a} onOpen={(att, url) => setViewer({ att, url })} />)}</div>}
      {files.length > 0 && <div className="flex flex-wrap gap-2">{files.map((a) => <FileRow key={a.id} subId={sub.id} att={a} />)}</div>}
      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-3xl rounded-3xl" data-testid="attachment-viewer">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-lg truncate">{viewer?.att.filename}</DialogTitle>
            <DialogDescription>Enviado por {sub.student_name}</DialogDescription>
          </DialogHeader>
          {viewer && isImage(viewer.att) && <img src={viewer.url} alt="" className="w-full max-h-[70vh] object-contain rounded-2xl bg-arc" />}
          {viewer && isVideo(viewer.att) && <video src={viewer.url} controls autoPlay className="w-full max-h-[70vh] rounded-2xl bg-black" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
